import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { noop } from 'lodash-es';
import { useCallback, useContext, useMemo } from 'react';

import CreatedColumn from '~components/Load/LoadsTableColumns/CreatedColumn';
import {
  ConfirmDeleteLoadAndTicketsDialog,
  useDeleteLoadAndTickets,
} from '~hooks/useDeleteLoadAndTickets/useDeleteLoadAndTickets';
import useEffectiveCompanyId from '~hooks/useEffectiveCompanyId/useEffectiveCompanyId';
import { Job } from '~hooks/useJob/models';
import { Load, LoadCycle } from '~hooks/useLoad/models';
import { Ticket } from '~hooks/useTickets/models';
import { useTickets } from '~hooks/useTickets/useTickets';
import { useStores } from '~store';
import { flaggedLoadRowSx } from '~utils/loads/load-utils';
import { isViewerAccountCustomer } from '~utils/user/user-utils';

import LoadsQueryContext from '../JobsTable/ExpandedBlock/LoadsQueryContext';
import ActionsColumn from './Columns/ActionsColumn';
import DeleteLoadColumn from './Columns/DeleteLoadColumn';
import DropoffTimeColumn from './Columns/DropoffTimeColumn';
import MaterialColumn from './Columns/MaterialColumn';
import NumberColumn from './Columns/NumberColumn';
import PhaseColumn from './Columns/PhaseColumn';
import PickupAndDropoffColumn from './Columns/PickupAndDropoffColumn';
import PickupTimeColumn from './Columns/PickupTimeColumn';
import QuantityColumn from './Columns/QuantityColumn';
import StatusAndCycleColumn from './Columns/StatusAndCycleColumn';
import TicketColumn from './Columns/TicketColumn';
import TicketTimeColumn from './Columns/TicketTimeColumn';
import { columns, gridTemplateColumns } from './loads-table-columns';

const rowGridColumn = `span ${columns.length}`;

interface TableRowProps {
  cycles: LoadCycle[];
  job: Job;
  load: Load;
  number: number;
  onClick: (loadId: string, ticket?: Ticket) => void;
  tickets: Ticket[];
}

export default function TableRow({
  cycles,
  job,
  load,
  number,
  onClick,
  tickets,
}: TableRowProps) {
  const theme = useTheme();

  const { userStore } = useStores();
  const companyId = useEffectiveCompanyId();

  const loadsQuery = useContext(LoadsQueryContext);

  const {
    confirmationDialogState,
    deleteLoadOrAlertIfItHasAssociatedTickets,
    isDeleting: isDeletingLoad,
  } = useDeleteLoadAndTickets({
    onLoadDeleted: () => loadsQuery.removeStoredLoadById(load.id),
    onTicketsDeleted: (idsOfDeletedTickets) => {
      loadsQuery.removeStoredTicketsById(idsOfDeletedTickets);
    },
  });

  const userPermissions = userStore.getPermissions();
  const { updateTicketPotentialDuplicate } = useTickets();

  const ticket = tickets.find(({ loadId }) => loadId === load.id);
  const ticketId = ticket?.id;
  const isReadOnlyTicket = isViewerAccountCustomer(userStore, job.order?.account);
  const canCreateTicket = !isReadOnlyTicket && userPermissions.canCreateTicket;
  const isLoadDeletable = !isReadOnlyTicket && userPermissions.canDeleteLoad;

  const isRowClickable = useMemo(() => {
    if (isDeletingLoad) {
      return false;
    }

    return ticket ? true : canCreateTicket;
  }, [isDeletingLoad, ticket, canCreateTicket]);

  const handleRowClick = useCallback(() => {
    if (!isDeletingLoad) {
      onClick(load.id, ticket);
    }
  }, [isDeletingLoad, load.id, ticket]);

  const handlePotentialDuplicateChange = useCallback(
    async (potentialDuplicate: boolean) => {
      if (!ticketId) {
        return;
      }

      await updateTicketPotentialDuplicate({
        companyId,
        id: ticketId,
        potentialDuplicate,
      });
      loadsQuery.refreshTicketById(ticketId);
      loadsQuery.refreshLoads();
    },
    [companyId, loadsQuery, ticketId, updateTicketPotentialDuplicate],
  );

  const handleDeleteLoadClick = useCallback(() => {
    if (isLoadDeletable) {
      deleteLoadOrAlertIfItHasAssociatedTickets(tickets, load.id);
    }
  }, [isLoadDeletable, load.id, tickets]);

  return (
    <Box
      data-test-id="approvals-files-table-row"
      onClick={isRowClickable ? handleRowClick : undefined}
      sx={(theme) => ({
        borderBottom: `solid 1px ${theme.brandV2.colors.treadGray7}`,
        display: 'grid',
        gridAutoRows: 'auto',
        gridColumn: rowGridColumn,
        gridTemplateColumns,
        overflow: 'hidden',
        ...(isRowClickable ? { cursor: 'pointer' } : {}),
        ...(ticket?.isPotentialDuplicateFlagged && flaggedLoadRowSx(theme)),
        ...(isDeletingLoad
          ? {
              bgcolor: theme.brandV2.colors.treadGray10,
              borderBottomColor: theme.brandV2.colors.treadGray6,
              opacity: 0.5,
              pointerEvents: 'none',
            }
          : {}),
      })}
    >
      <NumberColumn number={number} ticket={ticket} sx={{ ...columns[0].sx }} />
      <StatusAndCycleColumn
        sx={{ ...columns[1].sx }}
        load={load}
        job={job}
        cycles={cycles}
      />
      <TicketColumn
        job={job}
        onPotentialDuplicateChange={
          userPermissions.canEditTicketPotentialDuplicate
            ? handlePotentialDuplicateChange
            : undefined
        }
        sx={{ ...columns[2].sx }}
        ticket={ticket}
      />
      <QuantityColumn sx={{ ...columns[3].sx }} load={load} ticket={ticket} job={job} />
      <MaterialColumn sx={{ ...columns[4].sx }} load={load} ticket={ticket} job={job} />
      <PhaseColumn sx={{ ...columns[5].sx }} load={load} />
      <TicketTimeColumn sx={{ ...columns[6].sx }} ticket={ticket} />
      <CreatedColumn createdAt={ticket?.createdAt} sx={{ ...columns[7].sx }} />
      <PickupAndDropoffColumn sx={{ ...columns[8].sx }} load={load} />
      <PickupTimeColumn sx={{ ...columns[9].sx }} load={load} />
      <DropoffTimeColumn sx={{ ...columns[10].sx }} load={load} />
      <ActionsColumn
        sx={{ ...columns[11].sx }}
        load={load}
        ticket={ticket}
        isOrderEditable={Boolean(job.order?.editable)}
      />
      <DeleteLoadColumn
        isDeleting={isDeletingLoad}
        onDeleteLoadClick={isLoadDeletable ? handleDeleteLoadClick : undefined}
        ticket={ticket}
        sx={{ ...columns[12].sx }}
      />

      {load.notes && (
        <Box
          sx={(theme) => ({
            gridColumn: '2 / -2',
            py: 1,
            px: 1,
            mx: 1,
            mb: 1,
            backgroundColor: theme.brandV2.colors.treadGray8,
            borderRadius: theme.spacing(0.5),
          })}
        >
          <Typography
            variant="body2"
            color={theme.brandV2.colors.treadGray1}
            sx={{ whiteSpace: 'pre-line' }}
          >
            {load.notes}
          </Typography>
        </Box>
      )}

      <ConfirmDeleteLoadAndTicketsDialog
        isOpen={confirmationDialogState.isOpen}
        onClose={confirmationDialogState.isOpen ? confirmationDialogState.onClose : noop}
        onConfirm={
          confirmationDialogState.isOpen
            ? confirmationDialogState.onConfirm
            : () => Promise.resolve()
        }
      />
    </Box>
  );
}
